import { useQuery } from "@tanstack/react-query";
import { fetchDayReport } from "@/lib/services/companyService";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Stable query key helper for day report.
 */
export const dayReportKey = (date: string) => ["company", "day-report", date];

/**
 * Single source of truth for day-report query options.
 */
export const dayReportQueryOptions = (date: string | null | undefined) => ({
  queryKey: dayReportKey(date ?? ""),
  queryFn: () => fetchDayReport(date!),
  select: () => getCompanyRegistry().getDayReport(date!),
  staleTime: 30_000 as const, // was 0; saves invalidate this key precisely (doc 07 Phase B)
  refetchInterval: 60_000, // defensive liveness for passive viewers
  enabled: !!date,
});

/**
 * React Query hook for fetching and reading the day report for a specific date.
 * @param date - The date string (YYYY-MM-DD)
 */
export function useDayReport(date: string | null | undefined) {
  return useQuery(dayReportQueryOptions(date));
}

/**
 * Helper for components to prefetch without touching service-layer fetchers.
 */
export function prefetchDayReport(qc: QueryClient, date: string | null | undefined) {
  if (!date) return;           // nothing to prefetch
  return qc.prefetchQuery(dayReportQueryOptions(date));
}
